import { useEffect } from 'react';
import { setPageMode } from '@unisporkal/ga4';
import { useSuspenseQuery } from '@tanstack/react-query';
import useApi from '../useApi';
import { LOAD_QUERY_KEYS } from './keys';
import { useAsset } from '../../../hooks/stateful/useAsset';
import { useAcquisitionType } from '../../../hooks/stateful/useAcquisitionType';
import acquisitionTypes from '../../../support/acquisitionTypes';
import useDownloadModifiedAsset from '../../../hooks/useDownloadModifiedAsset';
import useRfSubscriptionStatusData from '../subscription/useRfSubscriptionStatusData';
import {
  type Asset,
  type UsagePrice,
  type LoadedLicenseHistory,
} from '../../../../types/isomorphic';

// Shape mirrors the hook's long-standing JSDoc return: config/configs keep an
// index signature so the ~50 callers that read them through nested `= {}`
// destructuring defaults still resolve. The raw payload is typed (LoadData);
// tightening config/configs here is deferred to the backend type-sharing work.
export interface UseOnLoadResult {
  response: {
    asset?: Asset;
    limitedUseProducts: UsagePrice[];
    licenseHistory: LoadedLicenseHistory;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    config: Record<string, any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    configs: Record<string, any>;
  };
  isLoading?: boolean;
}

const useOnLoad = (): UseOnLoadResult => {
  const { loadService } = useApi();
  const { asset, assets } = useAsset();
  const {
    genId,
    isUltrapackRedownloadable,
    index: genIndex,
  } = useDownloadModifiedAsset();

  const assetIds = assets?.map((singleAsset) => singleAsset.id);
  const agreementTypes: string[] = [];
  assets?.forEach((singleAsset) =>
    singleAsset.agreementDetails?.forEach((agreement) => {
      if (!agreementTypes.includes(agreement.Type)) {
        agreementTypes.push(agreement.Type);
      }
    })
  );

  if (isUltrapackRedownloadable && !!genId) {
    agreementTypes.push('IP');
  }

  const rfSubscriptionStatus = useRfSubscriptionStatusData(asset?.id);
  if (
    rfSubscriptionStatus?.hasRfSubscription &&
    !agreementTypes.includes('RF')
  ) {
    agreementTypes.push('RF');
  }

  const { acquisitionType, availableTypes, subscriptionOffer } =
    useAcquisitionType();

  // Sent to the server so buildOptions prices the right subscription
  // (an asset can be included only in a sub of a different family than its own).
  const subscriptionFamily =
    subscriptionOffer.kind === 'subscription'
      ? subscriptionOffer.subFamily
      : undefined;

  const acquisitionTypesOnly = availableTypes.map(
    (acquisitionTypeOnly) => acquisitionTypeOnly.type
  );

  const fetcher = async () => {
    const result = await loadService.getLoadData({
      acquisitionType,
      availableAcquisitionTypes: acquisitionTypesOnly,
      assetId: asset?.id,
      assetIds,
      assetType: asset?.assetType,
      licenseType: asset?.licenseType,
      agreementTypes,
      family: asset?.family,
      subscriptionFamily,
      genId,
      genIndex,
      isRfRedownloadable: rfSubscriptionStatus?.rfRedownloadEligible ?? false,
    });

    // PA Preview displays the sizes the asset is offered in
    if (
      acquisitionType === acquisitionTypes.PREVIEW &&
      result?.configs?.preview
    ) {
      (
        result.configs.preview as { options: Array<{ sizes?: unknown }> }
      ).options[0].sizes = result.asset?.sizes;
    }

    return result;
  };

  const queryData = useSuspenseQuery({
    queryKey: LOAD_QUERY_KEYS.onLoad(asset?.id, asset?.assetType, genId),
    queryFn: fetcher,
  });

  useEffect(() => {
    const isAgencyUnlimitedArtBuyer = (
      queryData?.data?.configs?.download as
        | { isAgencyUnlimitedArtBuyer?: boolean }
        | undefined
    )?.isAgencyUnlimitedArtBuyer;
    if (isAgencyUnlimitedArtBuyer) {
      setPageMode('agency_art_buyer');
    }
  }, [queryData]);

  return {
    ...queryData,
    response: {
      ...queryData?.data,
      config: {
        ...(queryData?.data?.configs?.[acquisitionType] as
          | Record<string, unknown>
          | undefined),
      },
    },
  } as UseOnLoadResult;
};

export default useOnLoad;
